import type { JournalEntry } from '@/db/journal'
import type { PlausibleEventOptions } from '@plausible-analytics/tracker'
import type { Ref } from 'vue'

let initPromise: Promise<void> | undefined

export function initPlausible(loggedIn: Ref<boolean>): void {
  // Initialize Plausible analytics if not already initialized (HMR can cause double initialization otherwise)
  if (initPromise || 'plausible' in window) {
    return
  }
  initPromise = import('@plausible-analytics/tracker').then(
    ({ init }) => {
      init({
        endpoint: '/v1/api/event',
        domain: 'clarityboss.com',
        formSubmissions: true,
        outboundLinks: true,
        customProperties: () => ({ logged_in: loggedIn.value ? 'true' : 'false' }),
        transformRequest: (payload) => {
          // replace any UUIDs in the URL with `[uuid]` so pages are better grouped
          payload.u = payload.u.replace(
            /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=[/?]|$)/gi,
            '/[uuid]',
          )
          return payload
        },
      })
    },
    (err) => {
      globalThis.reportError(err)
    },
  )
}

// Wrapper around the Plausible analytics tracker to allow dynamic imports and error handling if the tracker fails to load.
// The signature is identical, so it can be used as a drop-in replacement throughout the codebase.
export function track(eventName: string, options: PlausibleEventOptions): void {
  if (!import.meta.env.SSR) {
    import('@plausible-analytics/tracker').then(
      async ({ track }) => {
        if (initPromise) {
          await initPromise
        }
        track(eventName, options)
      },
      (err) => {
        globalThis.reportError(err)
      },
    )
  }
}

export function trackEntryCreated(entry: Pick<JournalEntry, 'type' | 'personId'>, source: string): void {
  track('Entry: Created', { props: { entryType: entry.type, hasPerson: String(!!entry.personId), source } })
}

export function trackEntryEdited(entry: Pick<JournalEntry, 'type' | 'personId'>, source: string): void {
  track('Entry: Edited', { props: { entryType: entry.type, hasPerson: String(!!entry.personId), source } })
}
